import { useRef } from 'react';
import { useLocation } from 'react-router';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useFeature } from '~/modules/root/hook/use-feature';
import { useEditPermissions } from '~/modules/user';
import { getSubRoutes } from '@modules/professional/component/sub-route-navigation/utils';
import type { ProfessionalInitialResult } from '@modules/professional/hook/use-professional-initial-route-data';
import { getActiveTabLink } from '~/ui-rocco/pages/professional-detail/utils/subnav-active';
import { useTabScrollAnchor } from '~/ui-rocco/pages/professional-detail/hooks/use-tab-scroll-anchor';
import { buildProfessionalNavItems } from '~/ui-rocco/pages/professional-detail/utils/professional-nav-items';
import { ProfessionalContactTabButton } from './ProfessionalContactTabButton';
import { useProfessionalLayoutData } from '@modules/professional/hook/use-professional-layout-data';

/** Match the Figma's tab labels (legacy uses Profile). */
const LABEL_OVERRIDES: Partial<Record<string, string>> = {
    Profile: 'Overview',
};

/**
 * Compact tab bar below the profile header — the professional's available tabs
 * (Overview / About / Projects / Products / Articles / Network / Contact …) as
 * a single full-bleed row that scrolls horizontally, the active tab marked by a
 * weight change and an underline (modern marketplace-profile style rather than
 * the old pill bar). Reuses {@link getSubRoutes} so only real tabs show. Lives
 * in the persistent layout so it renders once and never re-flashes on tab nav.
 */
export const ProfessionalSubNav = () => {
    const layoutData = useProfessionalLayoutData();
    const { pathname } = useLocation();
    const imageLabelSearchEnabled = useFeature('image_label_search_profile');
    const initial = layoutData?.initial;
    // Editors with Products allowed but no products yet still need the Products
    // tab to add their first product — mirror the legacy nav's edit permission.
    const { canEditProducts } = useEditPermissions(initial?.ID ?? 0);

    // Switching tabs leaves the scroll position alone — see useTabScrollAnchor.
    const navRef = useRef<HTMLElement>(null);
    useTabScrollAnchor(navRef);

    if (!initial) return null;

    const currentPath = pathname.replace(/\/+$/, '');
    const base = professionalTabBase(initial.URLSegment, currentPath);
    const professional = initial as unknown as ProfessionalInitialResult;
    // Match prod's tab set: pass linked-profile locations (Contact) and the
    // image-label flag (Spaces) through, exactly as the legacy nav does.
    const items = getSubRoutes(
        professional,
        canEditProducts,
        layoutData?.linkedProfiles?.linkedProfilesList,
        imageLabelSearchEnabled,
    );
    const navItems = buildProfessionalNavItems(items, {
        hasDownloads: professional.HasDownloads,
    });

    const tabLinks = navItems.map((item) =>
        `${base}${item.link}`.replace(/\/+$/, ''),
    );
    const activeTabLink = getActiveTabLink(tabLinks, currentPath);

    return (
        <nav
            ref={navRef}
            className="mt-6 border-b border-black/10 bg-ground md:mt-9"
        >
            {/* overflow-y-clip is load-bearing: overflow-x-auto alone coerces
                the y axis from visible to auto, making the bar a vertical
                scroll container that any sub-pixel overflow lets mobile wiggle
                and bounce. clip clips without ever scrolling. */}
            {/* The right-edge fade says the rail scrolls: with the scrollbar
                hidden, a tab clipped exactly at the viewport edge read as the
                end of the list, and Contact sat beyond it unfound. */}
            {/* The rail's own gap and gutter are 8px short of what the reader
                sees, because each tab carries that 8px itself — see the tab's
                `px-2`. Label to label is still 24px (32 from md), and the
                first label still starts on the page's 24px gutter (50 from
                md). */}
            <div className="mx-auto flex w-full max-w-(--ap-page-width) [scrollbar-width:none] items-center gap-2 overflow-x-auto overflow-y-clip px-4 max-md:[mask-image:linear-gradient(to_right,black_calc(100%-2.5rem),transparent)] md:gap-4 md:px-[2.625rem] [&::-webkit-scrollbar]:hidden">
                {navItems.map((item) => {
                    const to = `${base}${item.link}`.replace(/\/+$/, '');
                    const isActive = to === activeTabLink;
                    const tabClassName = cn(
                        // 8px of side padding the label does not need and the
                        // focus ring does: without it the ring ran along the
                        // letters. The rail gives the 8px back out of its own
                        // gap and gutter, so nothing moves — see the rail.
                        'relative shrink-0 px-2 py-4 font-sans whitespace-nowrap transition-colors max-md:text-[0.9375rem] md:text-base',
                        // The ring draws 1px outside the box by default, and
                        // the rail clips its own y axis (see above), so the
                        // top and bottom of the ring were cut and a focused
                        // tab read as two floating bars. Drawn inside the tab
                        // instead, it is whole and the clip stays.
                        'focus-visible:-outline-offset-2',
                        isActive
                            ? 'font-medium text-[#181818]'
                            : 'text-[#8a8a8a] hover:text-[#181818]',
                    );
                    if (item.opensEnquiry) {
                        return (
                            <ProfessionalContactTabButton
                                key={item.link}
                                className={tabClassName}
                            />
                        );
                    }
                    return (
                        <RoccoLink
                            key={item.link}
                            to={to}
                            // "intent", not "render" — see the desktop pill
                            // nav: nine rendered prefetches each re-run the
                            // overview-merged tab loader.
                            prefetch="intent"
                            // The router's top reset stands down: the effect
                            // above aligns the nav to the viewport top on tab
                            // change, keeping the reader at the tabs.
                            preventScrollReset
                            className={tabClassName}
                        >
                            {LABEL_OVERRIDES[item.label] ?? item.label}
                            {/* bottom-0 (not -bottom-px): the underline must
                                not poke past the scroller's bottom edge — that
                                1px of overflow is what made the bar vertically
                                scrollable. It sits flush above the nav's
                                hairline border instead. */}
                            {isActive ? (
                                <span
                                    aria-hidden="true"
                                    // inset-x-2, not 0: the tab's side padding
                                    // is the focus ring's, not the label's, so
                                    // the underline stops where the word does.
                                    className="absolute inset-x-2 bottom-0 h-[2px] rounded-full bg-[#181818]"
                                />
                            ) : null}
                        </RoccoLink>
                    );
                })}
            </div>
        </nav>
    );
};
